import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {IRepeatedData, VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {DnDDividerPlacementType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDDividerPlacementType';
import {DnDDropDirection} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDDropDirection';
import {
    ADragStartedEvent,
    ADropEvent
} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDEvents';
import {DnDHoveredDropTargetHighlightType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/dnd/DnDHoveredDropTargetHighlightType';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {SurfaceDnDContext, SurfacePoint} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {BindingId, BoxId, SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {FramePath, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import bind from 'bind-decorator';
import {css} from 'glamor';
import {action, runInAction} from 'mobx';
import {CSSProperties} from 'react';
import {DraggableData} from 'react-draggable';

interface IDropTarget
{
    box:Box;
    boxUniqueId:GUID;
    repeater:Repeater;
    bounds:ClientRect;
}

interface IBoundRepeaterData
{
    sheetId:SheetId;
    slotLocation:SlotLocation;
    pathInFrame:string;
}

interface ITriangle
{
    readonly a:SurfacePoint;
    readonly b:SurfacePoint;
    readonly c:SurfacePoint;
}

const leftTriangle:ITriangle = {
    a: new SurfacePoint(0, 0),
    b: new SurfacePoint(0.5, 0.5),
    c: new SurfacePoint(0, 1)
};

const topTriangle:ITriangle = {
    a: new SurfacePoint(0, 0),
    b: new SurfacePoint(1, 0),
    c: new SurfacePoint(0.5, 0.5)
};

const rightTriangle:ITriangle = {
    a: new SurfacePoint(1, 0),
    b: new SurfacePoint(1, 1),
    c: new SurfacePoint(0.5, 0.5)
};

export class SurfaceDnDController
{
    @action.bound
    private _setDropTarget(boxId:BoxId, boxUniqueId:GUID, repeater:Repeater, dropDirection:DnDDropDirection)
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {surfaceDnDState}}}}} = rootState;

        surfaceDnDState.dropTargetBoxId = boxId;
        surfaceDnDState.dropTargetBoxUniqueId = boxUniqueId;
        surfaceDnDState.dropTargetRepeater = repeater;
        surfaceDnDState.dropDirection = dropDirection;
    }

    private _getDropTarget(event:any):IDropTarget
    {
        const {
            ui: {
                visualDesigner: {
                    surfaceEditor: {
                        area: {
                            surfaceDnDState: {dragContext: {dndGroupName, boxUniqueId}},
                            repeatersCache
                        }
                    }
                }
            }
        } = rootState;
        const {designState} = commonControllers;
        let dropTarget:IDropTarget = {box: null, boxUniqueId: null, repeater: null, bounds: null};
        let element:HTMLElement = null;

        if( event.type && event.type.startsWith('touch') )
        {
            const touchEvent:TouchEvent = (event as TouchEvent);

            if( touchEvent.changedTouches.length > 0 )
                element = document.elementFromPoint(
                    touchEvent.changedTouches[0].clientX,
                    touchEvent.changedTouches[0].clientY
                ) as HTMLElement;
        }
        else
            element = ((event as MouseEvent).target as HTMLElement);

        if( element )
        {
            const dropTargetElement:HTMLElement = element.closest(
                `[data-box-dnd-group="${dndGroupName}"]:not([data-box-dnd-target="false"]):not([data-box-unique-id="${boxUniqueId}"])`
            ) as HTMLElement;

            if( dropTargetElement )
            {
                const currentBoxId:string = dropTargetElement.getAttribute('data-box-id');
                const currentBoxUniqueId:string = dropTargetElement.getAttribute('data-box-unique-id');
                const currentBox:Box = designState.findBoxFromId(currentBoxId);

                dropTarget = {
                    box: currentBox,
                    boxUniqueId: currentBoxUniqueId,
                    repeater: repeatersCache.get(currentBoxUniqueId),
                    bounds: dropTargetElement.getBoundingClientRect()
                };
            }
        }

        return dropTarget;
    }

    private _getBoundRepeaterData(box:Box, repeater:Repeater):IBoundRepeaterData
    {
        const {bindings, spreadsheets: {sheet}} = commonControllers;
        const data:IBoundRepeaterData = {
            sheetId: null,
            slotLocation: null,
            pathInFrame: null
        };

        if( box )
        {
            const bindingId:BindingId = box.componentDesc.repeaterProp.getBindingId(PropPath.fromString('values'));

            if( bindingId )
            {
                const binding = bindings.getPropBinding(bindingId);

                if( binding )
                {
                    const repeaterFixation = binding.repeaterFixation;
                    const {sheetId, location, framePath} = binding.range;

                    data.sheetId = sheetId;
                    data.slotLocation = location;

                    if( framePath )
                    {
                        data.pathInFrame = '';
                        if( framePath.length > 1 )
                        {
                            const slot = sheet.findSlot(sheetId, location);
                            const frame = slot ? slot.value as IFrame : null;

                            const newFramePathOrError = repeater.repeatPathInFrame(frame, framePath, repeaterFixation);
                            const newFramePath = Type.isInstanceOf(newFramePathOrError, Type.Error) ? [] : newFramePathOrError as FramePath;

                            data.pathInFrame = newFramePath.slice(0, -1).map(
                                (coord) => `[${coord.rowIndex}].${coord.fieldName}`
                            ).join('');
                        }
                    }
                }
            }
        }

        return data;
    }

    private _getPointPosition(a:SurfacePoint, b:SurfacePoint, d:SurfacePoint):number
    {
        return (d.x - a.x) * (b.y - a.y) - (d.y - a.y) * (b.x - a.x);
    }

    private _isPointsOnOneSide(a:SurfacePoint, b:SurfacePoint, c:SurfacePoint, d:SurfacePoint):boolean
    {
        return this._getPointPosition(a, b, c) * this._getPointPosition(a, b, d) >= 0;
    }

    private _isPointInTrianle(triangle:ITriangle, point:SurfacePoint):boolean
    {
        return this._isPointsOnOneSide(triangle.a, triangle.b, triangle.c, point)
            && this._isPointsOnOneSide(triangle.b, triangle.c, triangle.a, point)
            && this._isPointsOnOneSide(triangle.c, triangle.a, triangle.b, point);
    }

    private _normalizePoint(x:number, y:number, bounds:ClientRect):SurfacePoint
    {
        return new SurfacePoint(
            (x - bounds.left) / bounds.width,
            (y - bounds.top) / bounds.height,
        );
    }

    private _getDropDirection(x:number, y:number, dropTarget:IDropTarget):DnDDropDirection
    {
        const hoveredDropTargetHighlightType:DnDHoveredDropTargetHighlightType = dropTarget.box
            ? dropTarget.box.componentDesc.dndProp.value.hoveredDropTargetHighlightType
            : null;
        const dividerPlacementType:DnDDividerPlacementType = dropTarget.box
            ? dropTarget.box.componentDesc.dndProp.value.dividerPlacementType
            : null;

        if( dropTarget.bounds && DnDHoveredDropTargetHighlightType.isDivider(hoveredDropTargetHighlightType) )
        {
            if( DnDDividerPlacementType.isLeftRightEdge(dividerPlacementType) )
            {
                return x < (dropTarget.bounds.left + (dropTarget.bounds.width >> 1))
                    ? DnDDropDirection.Left
                    : DnDDropDirection.Right;
            }
            else if( DnDDividerPlacementType.isTopBottomEdge(dividerPlacementType) )
            {
                return y < (dropTarget.bounds.top + (dropTarget.bounds.height >> 1))
                    ? DnDDropDirection.Top
                    : DnDDropDirection.Bottom;
            }
            else if( DnDDividerPlacementType.isClosestEdge(dividerPlacementType) )
            {
                const point:SurfacePoint = this._normalizePoint(x, y, dropTarget.bounds);

                if( this._isPointInTrianle(leftTriangle, point) )
                    return DnDDropDirection.Left;
                else if( this._isPointInTrianle(topTriangle, point) )
                    return DnDDropDirection.Top;
                else if( this._isPointInTrianle(rightTriangle, point) )
                    return DnDDropDirection.Right;
                else
                    return DnDDropDirection.Bottom;
            }
        }

        return DnDHoveredDropTargetHighlightType.isSelf(hoveredDropTargetHighlightType)
            ? DnDDropDirection.Self
            : DnDDropDirection.None;
    }

    @action.bound
    public processDragStart(visualBox:VisualBox, repeater:Repeater, uniqueId:GUID, event:any)
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {surfaceDnDState}}}}} = rootState;

        if( event.target.tagName.toLowerCase() === 'input' )
        {
            event.stopPropagation();
            return;
        }

        const repeatedData:IRepeatedData = visualBox.repeatedData.get(repeater.index);

        if( repeatedData && repeatedData.dnd.isEnabled.value && repeatedData.isDraggable )
        {
            event.stopPropagation();
            event.preventDefault();

            surfaceDnDState.dragContext = new SurfaceDnDContext(
                event.nativeEvent,
                visualBox.box,
                uniqueId,
                repeater,
                visualBox.selfAlignedBounds.get(repeater.index),
                repeatedData.rotation,
                repeatedData.dndGroupName,
                repeatedData.dnd
            );
        }
    }

    @bind
    public processDragSource(curPoint:SurfacePoint, draggableRef)
    {
        const {surfaceArea} = commonControllers;
        const {ui: {visualDesigner: {surfaceEditor: {area: {surfaceDnDState}}}}} = rootState;
        const dragContext:SurfaceDnDContext = surfaceDnDState.dragContext;

        if( dragContext )
        {
            const {box, repeater, dndGroupName} = dragContext;
            const event = dragContext.mouseEvent;

            draggableRef.handleDragStart(event);

            const sourceRepeaterData:IBoundRepeaterData = this._getBoundRepeaterData(box, repeater);

            event.stopPropagation();
            event.preventDefault();

            setTimeout(() =>
            {
                surfaceArea.onBaseEvent(
                    box.componentDesc.onDragStartedEventProp,
                    repeater,
                    new ADragStartedEvent(
                        ADropEvent.getDnDDataFrame(
                            box.page.name,
                            box.componentDesc.name.value,
                            curPoint.x,
                            curPoint.y,
                            sourceRepeaterData.sheetId,
                            sourceRepeaterData.slotLocation,
                            sourceRepeaterData.pathInFrame,
                            repeater.index,
                            dndGroupName
                        )
                    )
                );
            });
        }
    }

    @bind
    public processDropTarget(event:MouseEvent, data:DraggableData, isDrop:boolean):void
    {
        const {surfaceArea} = commonControllers;
        const {ui: {visualDesigner: {surfaceEditor: {area: {surfaceDnDState: {dragContext}}}}}} = rootState;

        if( dragContext )
        {
            event.stopPropagation();
            event.preventDefault();

            const dropTarget:IDropTarget = this._getDropTarget(event);

            setTimeout(() =>
            {
                runInAction(() =>
                {
                    const {box, repeater, dndGroupName} = dragContext;
                    const sourceRepeaterData:IBoundRepeaterData = this._getBoundRepeaterData(box, repeater);
                    const targetRepeaterData:IBoundRepeaterData = this._getBoundRepeaterData(dropTarget.box, dropTarget.repeater);
                    const dropDirection:DnDDropDirection = this._getDropDirection(event.clientX, event.clientY, dropTarget);

                    if( isDrop )
                        this._setDropTarget(null, null, null, null);
                    else
                        this._setDropTarget(
                            dropTarget.box ? dropTarget.box.id : null,
                            dropTarget.boxUniqueId,
                            dropTarget.repeater,
                            dropDirection
                        );

                    surfaceArea.onBaseEvent(
                        isDrop ? box.componentDesc.onDropEventProp : box.componentDesc.onDragInProgressEventProp,
                        repeater,
                        new ADropEvent(
                            ADropEvent.getDnDDataFrame(
                                box.page.name,
                                box.componentDesc.name.value,
                                data.x - data.deltaX,
                                data.y - data.deltaY,
                                sourceRepeaterData.sheetId,
                                sourceRepeaterData.slotLocation,
                                sourceRepeaterData.pathInFrame,
                                repeater.index,
                                dndGroupName,
                                dropDirection
                            ),
                            ADropEvent.getDnDDataFrame(
                                dropTarget.box ? dropTarget.box.page.name : null,
                                dropTarget.box ? dropTarget.box.componentDesc.name.value : null,
                                data.x,
                                data.y,
                                targetRepeaterData.sheetId,
                                targetRepeaterData.slotLocation,
                                targetRepeaterData.pathInFrame,
                                dropTarget.repeater ? dropTarget.repeater.index : null,
                                dndGroupName,
                                dropDirection
                            )
                        )
                    );
                });
            });
        }
    }

    public getDropTargetDividerStyle(borderStyle:string = null):CSSProperties
    {
        const {
            ui: {
                visualDesigner: {
                    surfaceEditor: {
                        area: {
                            surfaceDnDState: {
                                dropDirection
                            }
                        }
                    }
                }
            }
        } = rootState;

        const borders:CSSProperties = {};

        if( DnDDropDirection.None === dropDirection )
        {
            borders.border = 'none';
        }
        else if( DnDDropDirection.Self === dropDirection )
        {
            if( borderStyle )
                borders.border = borderStyle;
        }
        else if( DnDDropDirection.Left === dropDirection )
        {
            if( borderStyle )
                borders.borderLeft = borderStyle;

            borders.borderTop = 'none';
            borders.borderRight = 'none';
            borders.borderBottom = 'none';
        }
        else if( DnDDropDirection.Top === dropDirection )
        {
            if( borderStyle )
                borders.borderTop = borderStyle;

            borders.borderLeft = 'none';
            borders.borderRight = 'none';
            borders.borderBottom = 'none';
        }
        else if( DnDDropDirection.Right === dropDirection )
        {
            if( borderStyle )
                borders.borderRight = borderStyle;

            borders.borderLeft = 'none';
            borders.borderTop = 'none';
            borders.borderBottom = 'none';
        }
        if( DnDDropDirection.Bottom === dropDirection )
        {
            if( borderStyle )
                borders.borderBottom = borderStyle;

            borders.borderLeft = 'none';
            borders.borderTop = 'none';
            borders.borderRight = 'none';
        }

        return borders;
    }

    public getDropTargetStyles():any
    {
        const {area: {surfaceDnDState: {dragContext}}} = rootState.ui.visualDesigner.surfaceEditor;
        let rules = {};

        if( dragContext )
        {
            rules = {
                [`& [data-box-dnd-group="${dragContext.dndGroupName}"][data-box-dnd-target="Self"]`]: {
                    '& > [data-box-dnd-mask="true"] > [data-box-dnd-mask-target="true"]': {
                        visibility: 'visible'
                    }
                },
                [`& [data-box-dnd-group="${dragContext.dndGroupName}"][data-box-unique-id="${dragContext.boxUniqueId}"]`]: {
                    '& > [data-box-dnd-mask="true"] > [data-box-dnd-mask-target="true"]': {
                        visibility: 'visible',
                        border: 'none !important',
                        background: 'rgba(255, 255, 255, 0.71) !important'
                    }
                }
            };
        }

        return rules ? css(rules) : null;
    }
}
